import React, { useEffect, useState } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { detachMultilinkedDocumentPartAsync, normalizePath } from "../../redux/feature/document/document-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectTenantId,
} from "../../redux/feature/settings/configurations-slice";
import {
  ElaborationStatusItem,
  elaborationStatusItemIsBlocking,
} from "../../redux/feature/object/remote-elaborations-slice";
import { DocumentData, EditDocument } from "./EditDocument";
import {
  fetchDocumentMetadata,
  fetchDocumentPartDetails,
  fetchParentsDocumentPart,
} from "../../api/document/DocumentsPart";
import { fetchDocumentPath } from "../../api/document/Documents";
import { getPathForFileLocationExplorer } from "../../utils/helpers/FileLocationPathHelper";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { DETACH_ELABORATION } from "../../utils/constants/notification";
import { defaultFormatting } from "../../utils/constants/formats";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import LoadingManager from "../../common/loading/LoadingManager";
import { getDocumentPartMetadataFromCustomXml } from "../../utils/helpers/CustomXmlHelper";

interface DetachProps {
  documentId: string;
  fatherDocumentId: string;
  onCloseModal: () => void;
  channelIdToNotify: string;
}

const findBlockingElaboration = (item: ElaborationStatusItem) =>
  item.elaborationType === "DETACH" && elaborationStatusItemIsBlocking(item);

export const DetachDocumentPanel: React.FunctionComponent<DetachProps> = (props) => {
  const username = useAppSelector(selectCpmUsername);
  const backendUrl = useAppSelector(selectBackendUrl);
  const tenantId = useAppSelector(selectTenantId);
  const clientId = useAppSelector(selectClientId);
  const dispatch = useAppDispatch();

  const [createContentControl, setCreateContentControl] = useState<boolean>(false);
  const [documentPartDetails, setDocumentPartDetails] = useState<{
    documentData: DocumentData;
    sourceDocumentName?: string;
  }>(null);

  useEffect(() => {
    const initDocumentPartDetails = async () => {
      const documentMetadata = await fetchDocumentMetadata(props.documentId);
      const inheritableMetadata = await getDocumentPartMetadataFromCustomXml();
      const documentPart = await fetchDocumentPartDetails(props.documentId);
      const parents = await fetchParentsDocumentPart(props.documentId);
      const isDetach = parents.length > 1 || (documentPart.root && parents.length === 1);

      let detachLocationPath = "";
      if (isDetach) {
        const rootAddress = (await fetchDocumentPath(props.fatherDocumentId)) ?? "";
        detachLocationPath = getPathForFileLocationExplorer(rootAddress);
      }

      const documentData: DocumentData = {
        description: documentPart.description,
        name: isDetach ? "" : documentPart.name,
        root: documentPart.root,
        multilinked: false,
        hasChildren: documentPart.hasChildren,
        path: isDetach ? detachLocationPath : documentPart.path,
        documentId: documentPart.id,
        siteId: documentPart.siteId,
        driveItemId: documentPart.driveItemId,
        advanced: {
          ...defaultFormatting,
          formatMetadata: {
            ...defaultFormatting.formatMetadata,
            locale: documentMetadata.formatMetadata?.locale,
          },
          templateInfo: documentMetadata.templateInfo,
        },
        language: documentMetadata.language,
        parameters: documentMetadata.parametersMetadata ?? [],
        inheritableParameters: inheritableMetadata?.parametersMetadata ?? [],
      };

      setDocumentPartDetails({
        sourceDocumentName: documentPart.name,
        documentData: documentData,
      });
      setCreateContentControl(isDetach);
    };

    void initDocumentPartDetails();
    return () => LoadingManager.remove(DETACH_ELABORATION);
  }, []);

  const blockingElaboration = useFilteredElaborations({
    findFunction: findBlockingElaboration,
  });

  const detachDocument = async (data: DocumentData) => {
    await detachMultilinkedDocumentPartAsync({
      createContentControl,
      documentId: props.documentId,
      fatherDocumentId: props.fatherDocumentId,
      channelIdToNotify: props.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      sharepointPath: normalizePath(data.path),
      username,
      tenantId,
      dispatch,
      backendUrl,
      documentPartMetadata: data.parameters,
      documentName: data.name,
      clientId,
      language: data.language,
      templateInfo: data.advanced.templateInfo,
      locale: data.advanced?.formatMetadata?.locale,
    });
    props.onCloseModal();
  };

  return (
    <ElaborationWrapper notificationKey={DETACH_ELABORATION} elaborationItem={blockingElaboration}>
      <>
        <HeaderPanel data-testid={"header"} applyPadding>
          {formatMessage("cof.documentmanagement.outline.detach.tile.detachdocument")}
        </HeaderPanel>

        {documentPartDetails && (
          <EditDocument
            onLastBackButton={() => props.onCloseModal()}
            isEdit={!createContentControl}
            editDocumentOperationType={"DETACH"}
            onSave={(data) => detachDocument(data)}
            getDocumentPartDetails={async () => documentPartDetails}
          />
        )}
      </>
    </ElaborationWrapper>
  );
};
